/**
 * HTMLView类
 * 负责DOM操作和UI更新
 */

import type { Position } from './types';
import { Tile } from './tile';
import { TILE_COLORS, TILE_TEXT_COLORS, CELL_SIZE, CELL_GAP } from './constants';
import { formatNumber } from './utils';

export class HTMLView {
  private tileContainer: HTMLElement;
  private scoreContainer: HTMLElement;
  private bestScoreContainer: HTMLElement;
  private messageContainer: HTMLElement;
  private tileElements: Map<number, HTMLElement>; // 使用Map存储tile ID到DOM元素的映射

  constructor() {
    this.tileContainer = this.getElement('.tile-container');
    this.scoreContainer = this.getElement('.score-container');
    this.bestScoreContainer = this.getElement('.best-score-container');
    this.messageContainer = this.getElement('.game-message');
    this.tileElements = new Map();
  }

  /**
   * 获取DOM元素
   * @param selector 选择器
   * @returns DOM元素
   */
  private getElement(selector: string): HTMLElement {
    const element = document.querySelector(selector);
    if (!element) {
      throw new Error(`Element not found: ${selector}`);
    }
    return element as HTMLElement;
  }

  /**
   * 清空方块容器
   */
  public clearContainer(): void {
    this.tileContainer.innerHTML = '';
    this.tileElements.clear();
  }

  /**
   * 添加方块到DOM
   * @param tile 方块实例
   */
  public addTile(tile: Tile): void {
    // 外层元素：负责定位
    const tileElement = document.createElement('div');
    tileElement.classList.add('tile', `tile-${tile.value}`);
    tileElement.setAttribute('data-tile-id', tile.id.toString());

    // 内层元素：负责内容显示和动画
    const tileInner = document.createElement('div');
    tileInner.classList.add('tile-inner');

    // 设置样式和文本到内层元素
    this.applyTileStyle(tileInner, tile.value);
    tileInner.textContent = tile.value.toString();

    // 设置初始位置（如果有previousPosition，从那里开始）
    const startPos = tile.previousPosition || tile.position;
    this.applyPosition(tileElement, startPos, false);

    // 组装元素
    tileElement.appendChild(tileInner);
    this.tileContainer.appendChild(tileElement);

    // 存储映射关系
    this.tileElements.set(tile.id, tileElement);

    // 强制重绘，确保动画生效
    void tileElement.offsetWidth;

    // 如果是新方块，添加出现动画到内层元素
    if (!tile.previousPosition && !tile.mergedFrom) {
      tileInner.classList.add('tile-new');
    }

    // 如果有previousPosition，应用移动动画到外层元素
    if (tile.previousPosition) {
      this.applyPosition(tileElement, tile.position, true);
    }

    // 如果是合并产生的方块，添加合并动画到内层元素
    if (tile.mergedFrom) {
      tileInner.classList.add('tile-merged');
      this.applyPosition(tileElement, tile.position, true);
    }
  }

  /**
   * 移动方块
   * @param tile 方块实例
   */
  public moveTile(tile: Tile): void {
    const tileElement = this.tileElements.get(tile.id);
    if (tileElement) {
      this.applyPosition(tileElement, tile.position, true);
    }
  }

  /**
   * 移除方块
   * @param tile 方块实例
   */
  public removeTile(tile: Tile): void {
    const tileElement = this.tileElements.get(tile.id);
    if (tileElement) {
      tileElement.classList.add('tile-remove');
      this.tileElements.delete(tile.id);
      // 延迟移除，等待动画完成
      setTimeout(() => {
        tileElement.remove();
      }, 200);
    }
  }

  /**
   * 应用位置样式
   * @param element DOM元素
   * @param position 位置
   * @param animate 是否应用动画过渡
   */
  private applyPosition(element: HTMLElement, position: Position, animate: boolean = true): void {
    // 从DOM获取实际的单元格尺寸
    const gridCell = document.querySelector('.grid-cell') as HTMLElement;
    const cellSize = gridCell ? gridCell.offsetWidth : CELL_SIZE;

    // 动态获取实际的gap值（通过测量相邻单元格的位置差）
    const gridCells = document.querySelectorAll('.grid-cell');
    const secondCell = gridCells[1] as HTMLElement;
    let cellGap = CELL_GAP; // 默认值

    if (gridCell && secondCell) {
      // 通过第二个单元格的左边距减去第一个单元格的左边距，再减去单元格宽度，得到实际gap
      cellGap = secondCell.offsetLeft - gridCell.offsetLeft - cellSize;
    }

    const x = position.x * (cellSize + cellGap);
    const y = position.y * (cellSize + cellGap);

    // 如果需要动画，添加transition类
    if (animate) {
      element.style.transition = 'transform 0.15s ease-in-out';
    } else {
      element.style.transition = 'none';
    }

    element.style.transform = `translate(${x}px, ${y}px)`;
  }

  /**
   * 应用方块样式
   * @param element DOM元素
   * @param value 方块数值
   */
  private applyTileStyle(element: HTMLElement, value: number): void {
    const bgColor = TILE_COLORS[value] ?? '#3c3a32';
    const textColor = TILE_TEXT_COLORS[value] ?? '#f9f6f2';

    element.style.backgroundColor = bgColor;
    element.style.color = textColor;

    // 检测是否为移动端
    const isMobile = window.innerWidth <= 520;

    // 根据数值大小和设备类型调整字体大小
    if (isMobile) {
      // 移动端字体大小
      if (value >= 1000) {
        element.style.fontSize = '24px';
      } else if (value >= 100) {
        element.style.fontSize = '28px';
      } else {
        element.style.fontSize = '30px';
      }
    } else {
      // 桌面端字体大小
      if (value >= 1000) {
        element.style.fontSize = '32px';
      } else if (value >= 100) {
        element.style.fontSize = '40px';
      } else {
        element.style.fontSize = '48px';
      }
    }
  }

  /**
   * 更新分数显示
   * @param score 当前分数
   */
  public updateScore(score: number): void {
    this.scoreContainer.textContent = formatNumber(score);
  }

  /**
   * 更新最高分显示
   * @param score 最高分
   */
  public updateBestScore(score: number): void {
    this.bestScoreContainer.textContent = formatNumber(score);
  }

  /**
   * 显示游戏消息
   * @param message 消息文本
   * @param type 消息类型
   */
  public showMessage(message: string, type: 'win' | 'lose'): void {
    this.messageContainer.classList.add(type === 'win' ? 'game-won' : 'game-over');
    const messageText = this.messageContainer.querySelector('p');
    if (messageText) {
      messageText.textContent = message;
    }
  }

  /**
   * 隐藏游戏消息
   */
  public hideMessage(): void {
    this.messageContainer.classList.remove('game-won', 'game-over');
  }

  /**
   * 渲染网格（显示所有方块）
   * @param tiles 方块数组
   */
  public render(tiles: Tile[]): void {
    this.clearContainer();
    tiles.forEach((tile) => this.addTile(tile));
  }
}
